import { IconButton, InputAdornment, TextField } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { Autocomplete as GooglePlacesAutocomplete } from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import "./Sitetypes.css";

const getComponent = (components, type) =>
  components?.find((comp) => comp.types.includes(type))?.long_name ?? "";

const getStreetDetail = (components) => {
  const streetNumber = getComponent(components, "street_number");
  const route = getComponent(components, "route");
  const unit = getComponent(components, "subpremise");
  const streetParts = [streetNumber, route].filter(Boolean);
  let detail = streetParts.join(" ");
  if (!detail) {
    detail = getComponent(components, "premise") || getComponent(components, "locality");
  }
  if (unit) {
    detail = `${detail} #${unit}`;
  }
  return detail || "";
};

const SearchMap = (props) => {
  const { setSearchTerm, searchTerm } = props;
  const shouldUseDetailedLabel = Boolean(props.useDetailedLabel);
  const [autocomplete, setAutocomplete] = useState(null);
  const [inputField, setInputField] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const adjustDropdownWidth = () => {
      if (!inputField) return;

      const dropdowns = document.querySelectorAll(".pac-container");

      dropdowns.forEach((dropdown) => {
        dropdown.style.width = `${inputField.offsetWidth}px`;
        dropdown.style.minWidth = `${inputField.offsetWidth}px`;
      });
    };

    // Observe for dropdown creation and adjust width
    const observer = new MutationObserver(() => adjustDropdownWidth());
    observer.observe(document.body, { childList: true, subtree: true });

    // Adjust on window resize
    window.addEventListener("resize", adjustDropdownWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", adjustDropdownWidth);
    };
  }, [inputField]);

  // Detect correct MUI Input field dynamically
  const handleFocus = () => {
    if (inputRef.current) {
      const detectedInputField = inputRef.current.closest(".MuiFormControl-root");
      setInputField(detectedInputField);
    }
  };

  // Trigger focus when the country is changed
  // useEffect(() => {
  //   if (props?.changeCountry && inputRef.current) {
  //     inputRef.current.focus(); // Programmatically focus the input
  //     handleFocus(); // Call handleFocus to detect dynamic CSS class
  //   }
  // }, [props.changeCountry]); // Runs when changeCountry changes

  const onLoadAuto = (newInstance) => {
    setAutocomplete(newInstance);
  };

  const handleSearch = async () => {
    let place = autocomplete.getPlace();
    if (place?.geometry) {
      const streetDetail = getStreetDetail(place.address_components);
      const detailedLabel = streetDetail || place.formatted_address;
      if (shouldUseDetailedLabel) {
        setSearchTerm(detailedLabel);
        props.handleSearch({ ...place, streetDetail, detailedLabel });
      } else {
        setSearchTerm(place.formatted_address);
        props.handleSearch(place);
      }
    }
  };

  const triggerClear = () => {
    setSearchTerm("");
    if (props.onClear) {
      props.onClear();
    }
  };

  const handleSearchText = (e) => {
    let inputValue = e.target.value;
    // Block input if it starts with non-alphanumeric (e.g. _ or space)
    if (/^[^A-Za-z0-9]+/.test(inputValue)) {
      inputValue = "";
      if (inputRef.current) inputRef.current.value = "";
    }
    setSearchTerm(inputValue);
    if (!inputValue) {
      triggerClear();
    }
  };

  const handleClose = () => {
    triggerClear();
  };

  const handleKeyDown = (e) => {
    // If the input is empty and the key is not alphanumeric, block it
    if (
      searchTerm.length === 0 &&
      !/^[A-Za-z0-9]$/.test(e.key) &&
      e.key.length === 1 // Only block printable characters
    ) {
      e.preventDefault();
    }
  };

  const googleOptions = {
    componentRestrictions: {
      country: props?.countryCode ? [props?.countryCode] : ["us", "ca", "gb", "in"]
    }
  };
  if (shouldUseDetailedLabel) {
    googleOptions.fields = ["formatted_address", "address_components", "geometry"];
    googleOptions.types = ["address"];
  } else if (props?.from === "jobposting") {
    googleOptions.types = ["(cities)"];
  }

  return (
    <>
      <GooglePlacesAutocomplete
        onLoad={(newInstance) => onLoadAuto(newInstance)}
        onPlaceChanged={handleSearch}
        options={googleOptions}>
        <TextField
          // autoFocus={props?.changeCountry} // Autofocus when changing country
          type={"text"}
          variant={props?.from === "map" ? "standard" : "outlined"}
          placeholder={props?.placeholder}
          className="search-location"
          fullWidth
          error={props?.error || false}
          size={"small"}
          value={searchTerm}
          onChange={(event) => handleSearchText(event)}
          onFocus={handleFocus} // Ensure focus detection works
          inputRef={inputRef} // Attach ref to detect element dynamically
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiInput-underline:before, & .MuiInput-underline:after": {
              borderBottom: "none"
            },
            "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
              borderBottom: "none"
            },
            "& .MuiInputBase-root": {
              borderBottom: "none"
            },
            "& .MuiInputBase-input": {
              padding: "10px",
              margin: "0",
              paddingLeft: "10px"
            }
          }}
          style={props?.style}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleClose()}>
                    {searchTerm === "" ? <SearchIcon /> : <CloseIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />
      </GooglePlacesAutocomplete>
    </>
  );
};

export default SearchMap;
